import React from 'react';
import Viewport from './Viewport';

export const KAIROS_VIEWPORT_SLOT_ISOLATION_V1 = 'KAIROS_VIEWPORT_SLOT_ISOLATION_V1';

let lastViewportInstanceEpoch = 0;
const allocateViewportInstanceEpoch = () => {
  lastViewportInstanceEpoch += 1;
  if (!Number.isSafeInteger(lastViewportInstanceEpoch)) lastViewportInstanceEpoch = 1;
  return lastViewportInstanceEpoch;
};

export function DiagnosticViewportSlotImpl({
  index,
  initialSeries,
  toolMode,
  initialWL,
  slotLabel,
  onSliceChange,
  onInteractionPerformanceEvent,
  onLinkedSliceRequest,
  onToolComplete,
  linkedScrollFraction,
  linkedScrollCommandId,
  linkedScrollCommandChannel,
  linkedScrollEnabled,
  linkedAuthorityEpoch,
  syncCommand,
  publishMultiMonitor,
  focusReportViewport,
  studyInstanceUID,
  setViewportWindowLevels,
  setViewportZoomFit,
  setViewportFreezeState,
  onReportContextChange,
  handleViewportSeriesLoadedForLinkedScroll,
  setActiveSeriesId,
  showRefLines,
  referenceGeometryChannel,
  onViewportGeometryChange,
  overlayConfig,
  annotationAppearance,
  onAnnotationAppearanceChange,
  studyInfo,
  comparisonContext,
  isActiveViewport,
  isViewportMaximized,
  viewportLayoutMode,
  onViewportDoubleClick,
  keyboardToolShortcuts,
  toolLayoutPrefs,
  toolCompletionBehavior,
  escapeReturnsToScroll,
  triangulationPoint,
  onTriangulationPoint,
  triangulationDragChannel,
  keyObjects,
  onMarkKeyObject,
  cineResourceGovernor,
  cacheEpoch,
}) {
  const viewportInstanceEpochRef = React.useRef(null);
  if (viewportInstanceEpochRef.current == null) {
    viewportInstanceEpochRef.current = allocateViewportInstanceEpoch();
  }
  const handleViewportFocus = React.useCallback(() => {
    publishMultiMonitor('viewport_focus', { viewportIndex: index, studyInstanceUID });
    focusReportViewport(index);
  }, [focusReportViewport, index, publishMultiMonitor, studyInstanceUID]);

  const handleWindowLevelChange = React.useCallback((wl) => {
    setViewportWindowLevels(prev => ({ ...prev, [index]: wl }));
    publishMultiMonitor('window_level', { viewportIndex: index, wl, studyInstanceUID });
  }, [index, publishMultiMonitor, setViewportWindowLevels, studyInstanceUID]);

  const handleViewportZoomChange = React.useCallback((zoom) => {
    setViewportZoomFit(prev => ({ ...prev, [index]: zoom }));
  }, [index, setViewportZoomFit]);

  const handleViewportFreezeChange = React.useCallback((frozen) => {
    setViewportFreezeState(prev => ({ ...prev, [index]: frozen }));
  }, [index, setViewportFreezeState]);

  const handleSeriesLoad = React.useCallback((series) => {
    handleViewportSeriesLoadedForLinkedScroll(index);
    if (index === 0) setActiveSeriesId(series.seriesInstanceUID);
    setViewportWindowLevels(prev => ({ ...prev, [index]: initialWL || { ww: 0, wc: 0 } }));
    publishMultiMonitor('series_loaded', { viewportIndex: index, series, studyInstanceUID });
  }, [handleViewportSeriesLoadedForLinkedScroll, index, initialWL, publishMultiMonitor, setActiveSeriesId, setViewportWindowLevels, studyInstanceUID]);

  return (
    <Viewport
      index={index}
      initialSeries={initialSeries}
      toolMode={toolMode}
      initialWL={initialWL}
      slotLabel={slotLabel}
      onSliceChange={onSliceChange}
      onInteractionPerformanceEvent={onInteractionPerformanceEvent}
      onLinkedSliceRequest={onLinkedSliceRequest}
      onToolComplete={onToolComplete}
      linkedScrollFraction={linkedScrollFraction}
      linkedScrollCommandId={linkedScrollCommandId}
      linkedScrollCommandChannel={linkedScrollCommandChannel}
      linkedScrollEnabled={linkedScrollEnabled}
      linkedAuthorityEpoch={linkedAuthorityEpoch}
      syncCommand={syncCommand}
      onViewportFocus={handleViewportFocus}
      onWindowLevelChange={handleWindowLevelChange}
      onViewportZoomChange={handleViewportZoomChange}
      onViewportFreezeChange={handleViewportFreezeChange}
      onReportContextChange={onReportContextChange}
      onSeriesLoad={handleSeriesLoad}
      referenceGeometryChannel={referenceGeometryChannel}
      showRefLines={showRefLines}
      studyInstanceUID={studyInstanceUID}
      onViewportGeometryChange={onViewportGeometryChange}
      overlayConfig={overlayConfig}
      annotationAppearance={annotationAppearance}
      onAnnotationAppearanceChange={onAnnotationAppearanceChange}
      studyInfo={studyInfo}
      comparisonContext={comparisonContext}
      isActiveViewport={isActiveViewport}
      isViewportMaximized={isViewportMaximized}
      viewportLayoutMode={viewportLayoutMode}
      onViewportDoubleClick={onViewportDoubleClick}
      keyboardToolShortcuts={keyboardToolShortcuts}
      toolLayoutPrefs={toolLayoutPrefs}
      toolCompletionBehavior={toolCompletionBehavior}
      escapeReturnsToScroll={escapeReturnsToScroll}
      triangulationPoint={triangulationPoint}
      onTriangulationPoint={onTriangulationPoint}
      triangulationDragChannel={triangulationDragChannel}
      keyObjects={keyObjects}
      onMarkKeyObject={onMarkKeyObject}
      cineResourceGovernor={cineResourceGovernor}
      viewportInstanceEpoch={viewportInstanceEpochRef.current}
      cacheEpoch={cacheEpoch}
    />
  );
}

const DiagnosticViewportSlot = React.memo(DiagnosticViewportSlotImpl);
DiagnosticViewportSlot.displayName = 'DiagnosticViewportSlot';

export default DiagnosticViewportSlot;
